import { Link } from '@tanstack/react-router';
import { ArrowRight, Mail } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PurchaseOrderMainProductionDeclarationStatusBadge } from '@/features/purchase-order/components/purchase-order-production-declaration-status-badge';
import { getUnsignedProformaWaitingForSupplierAgreementDefaultMessage } from '@/features/purchase-order/config/purchase-order-send-default-message';
import { PurchaseOrderMainProductionDeclarationEmailDialog } from '@/features/purchase-order/purchase-order-main-production-declaration-email-dialog';
import { getProductionDeclarationDataError } from '@/services/apis/errors/production-declaration-data-error';
import { useCreateProductionDeclaration } from '@/services/apis/mutations/purchase-orders/use-create-production-declaration';
import { useGetPurchaseOrder } from '@/services/apis/queries/purchase-order/use-get-purchase-order';

type PurchaseOrderMainProductionDeclarationProps = {
  purchaseOrderId: string;
};

export function PurchaseOrderMainProductionDeclaration({
  purchaseOrderId,
}: PurchaseOrderMainProductionDeclarationProps) {
  const { t } = useTranslation('purchaseOrder');
  const { data: purchaseOrder } = useGetPurchaseOrder(purchaseOrderId);
  const createDeclaration = useCreateProductionDeclaration(purchaseOrderId);
  const productionDeclarationId = purchaseOrder.productionDeclarationId;
  const productionDeclarationStatus = purchaseOrder.productionDeclarationStatus;
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const supplierPortalLink =
    productionDeclarationId && purchaseOrder.productionDeclarationToken
      ? `${window.location.origin}/production-declarations/${productionDeclarationId}?token=${encodeURIComponent(purchaseOrder.productionDeclarationToken)}`
      : null;

  const initialRecipientLabels: Record<string, string> = {};
  const initialRecipientEmails = (purchaseOrder.supplier.contacts ?? [])
    .map((contact) => {
      const email = contact.email?.trim();
      const name = contact.name?.trim();
      if (!email) {
        return null;
      }
      if (name) {
        initialRecipientLabels[email] = name;
      }
      return email;
    })
    .filter((email): email is string => Boolean(email));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('cardProductionDeclaration')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='flex items-center justify-between gap-3'>
          {productionDeclarationId ? (
            <>
              {productionDeclarationStatus && (
                <PurchaseOrderMainProductionDeclarationStatusBadge
                  status={productionDeclarationStatus}
                />
              )}
              <div className='flex items-center gap-2'>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label={t('productionDeclarationEmailButtonAria')}
                      onClick={() => setOpenEmailDialog(true)}
                      size='icon'
                      type='button'
                      variant='outline'
                    >
                      <Mail className='size-4' />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('sendProductionDeclaration')}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button asChild size='icon' variant='outline'>
                      <Link
                        aria-label={t('productionDeclarationLinkButtonAria')}
                        params={{ id: productionDeclarationId }}
                        to='/production-declarations/$id/statement'
                      >
                        <ArrowRight className='size-4' />
                      </Link>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('openProductionDeclarationTooltip')}
                  </TooltipContent>
                </Tooltip>
              </div>
            </>
          ) : (
            <p>{t('noProductionDeclaration')}</p>
          )}
        </div>
        {!productionDeclarationId && (
          <Button
            className='mt-4'
            disabled={createDeclaration.isPending}
            onClick={() =>
              createDeclaration.mutate(undefined, {
                onSuccess: () =>
                  toast.success(t('createProductionDeclarationSuccess')),
                onError: (error) =>
                  toast.error(t('createProductionDeclarationError'), {
                    description: getProductionDeclarationDataError(error, t),
                    duration: Number.POSITIVE_INFINITY,
                  }),
              })
            }
            type='button'
            variant='outline'
          >
            {t(
              createDeclaration.isPending
                ? 'creatingProductionDeclaration'
                : 'createProductionDeclaration',
            )}
          </Button>
        )}
      </CardContent>
      {openEmailDialog && (
        <PurchaseOrderMainProductionDeclarationEmailDialog
          initialMessage={getUnsignedProformaWaitingForSupplierAgreementDefaultMessage(
            purchaseOrder.supplier.name,
            supplierPortalLink,
          )}
          initialRecipientLabels={initialRecipientLabels}
          initialRecipients={initialRecipientEmails}
          onOpenChange={setOpenEmailDialog}
          purchaseOrderId={purchaseOrderId}
        />
      )}
    </Card>
  );
}
